import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

/**
 * Merge Tailwind CSS class names with clsx + tailwind-merge,
 * resolving conflicts in favour of the later class.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs))
}

/**
 * Format a numeric price as a Dutch-locale currency string.
 * Example: 4.95 → "€ 4,95"
 */
export function formatPrice(price: number): string {
  return new Intl.NumberFormat('nl-NL', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(price)
}

/**
 * Format a Date or ISO date string as a Dutch locale date string.
 * Example: new Date('2024-12-25') → "25 december 2024"
 */
export function formatDate(date: Date | string): string {
  return new Intl.DateTimeFormat('nl-NL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(typeof date === 'string' ? new Date(date) : date)
}

/**
 * Convert a string to a URL-safe slug.
 * Example: "Vers Witbrood!" → "vers-witbrood"
 */
export function slugify(str: string): string {
  return str
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
    .replace(/[^a-z0-9\s-]/g, '')    // Remove non-alphanumeric chars
    .trim()
    .replace(/\s+/g, '-')            // Replace spaces with hyphens
    .replace(/-+/g, '-')             // Collapse multiple hyphens
}

/**
 * Truncate a string to the given length, appending "…" if needed.
 */
export function truncate(str: string, length: number): string {
  if (str.length <= length) return str
  return str.slice(0, length).trimEnd() + '…'
}

/**
 * Calculate the discount percentage between an original price and a sale price.
 * Returns a whole-number percentage, e.g. 25 for a 25% discount.
 */
export function calculateDiscount(original: number, sale: number): number {
  if (original <= 0) return 0
  return Math.round(((original - sale) / original) * 100)
}

/**
 * Determine whether a product is currently on sale.
 * Returns true if salePrice exists and, if saleEndDate is set, that date has not yet passed.
 */
export function isOnSale(product: {
  salePrice?: number | null
  saleEndDate?: Date | string | null
}): boolean {
  if (!product.salePrice) return false
  if (!product.saleEndDate) return true
  return new Date(product.saleEndDate) > new Date()
}

/** Hoe lang een product standaard als nieuw geldt: twee weken. */
export const DEFAULT_NEW_DAYS = 14

export type NewWindow = {
  isNew?: boolean
  newFrom?: Date | string | null
  newUntil?: Date | string | null
}

/** Datum als "YYYY-MM-DDTHH:mm" in lokale tijd, het formaat van DateTimePicker. */
export function toDateTimeInput(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`
    + `T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

/**
 * Standaard einddatum bij het vinkje "Nieuw": twee weken na de startdatum
 * (of na nu, als er geen startdatum is gekozen).
 */
export function defaultNewUntil(from?: Date | string | null): Date {
  const start = from ? new Date(from) : new Date()
  return new Date(start.getTime() + DEFAULT_NEW_DAYS * 86_400_000)
}

/**
 * Determine whether a product should be shown with a "New" badge.
 * The flag must be set, the optional start date must have arrived and the
 * optional end date must not have passed.
 */
export function isNew(product: NewWindow): boolean {
  return isNewProduct(product)
}

/**
 * Safely parse a JSON field that is stored as a string in the database.
 * Returns an empty array when the field is null/undefined or invalid JSON.
 */
export function parseJsonField<T = unknown>(field: string | null | undefined): T[] {
  if (!field) return []
  try {
    const parsed = JSON.parse(field)
    return Array.isArray(parsed) ? (parsed as T[]) : []
  } catch {
    return []
  }
}

/**
 * Named isNewProduct to avoid collision with the built-in isNew helper.
 * True when the "Nieuw" flag is set and today falls inside the optional
 * newFrom/newUntil window.
 */
export function isNewProduct(product: NewWindow): boolean {
  if (!product.isNew) return false
  const now = Date.now()
  if (product.newFrom && new Date(product.newFrom).getTime() > now) return false
  if (product.newUntil && new Date(product.newUntil).getTime() <= now) return false
  return true
}

/**
 * Extract the first image URL from a JSON-encoded images field.
 * Falls back to a placeholder image if the field is empty or unparseable.
 */
export function getProductImage(images: string | null | undefined): string {
  const PLACEHOLDER = '/images/placeholder-product.jpg'
  if (!images) return PLACEHOLDER
  try {
    const parsed = JSON.parse(images)
    if (Array.isArray(parsed) && parsed.length > 0 && typeof parsed[0] === 'string') {
      return parsed[0]
    }
    if (typeof parsed === 'string') return parsed
    return PLACEHOLDER
  } catch {
    // Might be a plain URL string, not JSON
    if (images.startsWith('http') || images.startsWith('/')) return images
    return PLACEHOLDER
  }
}

/** Opties/keuzes zoals ze bij een besteld product zijn opgeslagen
 *  (OrderItem.customizations is JSON). Nooit laten crashen op rommel. */
export interface StoredCustomization {
  groupName:  string
  optionName: string
  priceAddon?: number
}

export function parseCustomizations(json: string | null | undefined): StoredCustomization[] {
  if (!json) return []
  try {
    const parsed = JSON.parse(json)
    if (!Array.isArray(parsed)) return []
    return parsed.filter(
      (c): c is StoredCustomization =>
        !!c && typeof c.groupName === 'string' && typeof c.optionName === 'string',
    )
  } catch {
    return []
  }
}
